import Link from "next/link";
import { useState } from "react";
import { useAnalytics } from "~/components/Analytics";
import { ContentItemProps } from "~/components/ContentItem";
import { FormattedDate } from "~/components/FormattedDate";
import { ChartPackLabel } from "~/components/ChartPackLabel";
import { ReportEntryEvents } from "~/components/ReportLibraryEntryPage/reportLibraryEntryEvents";
import { Body, Headline, PillButton } from "~/ui-library";

export type ReportLibraryRecentlyPublishedPost = Pick<
  ContentItemProps,
  "headline" | "headlineHref" | "id" | "date" | "kicker" | "kickerHref"
> & {
  isChartPack?: boolean;
};

export type ReportLibraryRecentlyPublishedTab = {
  title: string;
  slug: string;
  posts: ReportLibraryRecentlyPublishedPost[];
};

export type ReportLibraryEntryRecentlyPublishedProps = {
  title: string;
  tabs: ReportLibraryRecentlyPublishedTab[];
};

export const ReportLibraryEntryRecentlyPublished: React.FC<
  ReportLibraryEntryRecentlyPublishedProps
> = ({ tabs, title }) => {
  const [selectedTab, setSelectedTab] = useState(0);
  const { analyticsEnhancedTrack } = useAnalytics();

  return (
    <div className="flex flex-col gap-y-m">
      {!!title && (
        <Headline level={3} className="text-gray-900">
          {title}
        </Headline>
      )}
      <div className="flex flex-wrap gap-xs">
        {tabs.map(({ slug, title: tabTitle }, idx) => (
          <PillButton
            appearance={idx === selectedTab ? "selected" : "default"}
            fontSize="default"
            key={`${slug}-${idx}`}
            onClick={() => {
              if (selectedTab !== idx) {
                setSelectedTab(idx);
                analyticsEnhancedTrack(
                  ReportEntryEvents.RecentlyPublishedTabClicked,
                  {
                    tabName: tabTitle,
                  }
                );
              }
            }}
          >
            {tabTitle}
          </PillButton>
        ))}
      </div>

      <div className="flex flex-col">
        {tabs[selectedTab]?.posts.map((post, idx) => (
          <div
            className="mb-s grid border-b-1 border-gray-300 pb-s last:mb-0 last:border-b-0 last:pb-0 md:grid-cols-[120px,auto,auto,auto] md:border-b-0 md:pb-0"
            key={idx}
          >
            <Body className="mb-xs w-fit py-0 pr-0 md:mb-0 md:py-s md:pr-m">
              {post.date ? (
                <FormattedDate
                  date={new Date(post.date)}
                  dateFormat="MM/dd/yyyy"
                />
              ) : null}
            </Body>

            <Headline
              level={5}
              className="col-span-2  mb-xs w-full py-0 pr-0 md:mb-0 md:py-s md:pr-m"
            >
              {post.headlineHref ? (
                <Link
                  href={post.headlineHref}
                  onClick={() =>
                    analyticsEnhancedTrack(
                      ReportEntryEvents.RecentlyPublishedLinkClicked,
                      {
                        reportName: post.headline,
                        reportLink: post.headlineHref,
                      }
                    )
                  }
                >
                  {post.headline}
                </Link>
              ) : (
                post.headline
              )}
            </Headline>

            <Body className="mb-xs flex w-full shrink gap-m whitespace-nowrap py-0 pl-0 text-end text-gray-700 md:mb-0 md:justify-end md:py-s md:pl-m">
              {post.isChartPack ? (
                <ChartPackLabel bodySize="small" iconSize="small" />
              ) : null}

              {post.kickerHref ? (
                <Body size="small">
                  <Link href={post.kickerHref}>{post.kicker}</Link>
                </Body>
              ) : (
                <Body size="small">{post.kicker}</Body>
              )}
            </Body>
          </div>
        ))}
      </div>
    </div>
  );
};
